import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSuperChatLogStore } from "@/store/useSuperChatLogStore";

/**
 * 채팅 로그 리스트 커스텀 훅
 *
 * @description 채팅 로그 리스트의 비즈니스 로직을 관리하는 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
const useList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { exportToExcel } = useSuperChatLogStore();

  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(searchParams.get("limit") || "10", 10)
  );
  const [filterValues, setFilterValues] = useState({
    search: searchParams.get("search") || "",
    sessionType: searchParams.get("sessionType") || "",
    sessionUserType: searchParams.get("sessionUserType") || "",
    status: searchParams.get("status") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
    feedbackFilter: searchParams.get("feedbackFilter") || "",
  });

  /**
   * 페이지 변경 처리
   */
  const handlePageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", String(newPage + 1)); // Material-UI는 0부터 시작하므로 +1
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  /**
   * 페이지 크기 변경 처리
   */
  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);

      const params = new URLSearchParams(searchParams);
      params.set("page", "1"); // 페이지 크기 변경 시 첫 페이지로
      params.set("limit", String(newRowsPerPage));
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  /**
   * 필터 변경 처리
   */
  const handleFilterChange = useCallback(
    (newFilterValues: typeof filterValues) => {
      setFilterValues(newFilterValues);

      const params = new URLSearchParams(searchParams);
      params.set("page", "1"); // 필터 변경 시 첫 페이지로

      // 각 필터 값을 URL 파라미터에 설정
      Object.entries(newFilterValues).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });

      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  /**
   * 필터 초기화 처리
   */
  const handleResetFilters = useCallback(() => {
    const defaultValues = {
      search: "",
      sessionType: "",
      sessionUserType: "",
      status: "",
      startDate: "",
      endDate: "",
      feedbackFilter: "",
    };
    setFilterValues(defaultValues);

    const params = new URLSearchParams();
    params.set("page", "1");
    setSearchParams(params);
  }, [setSearchParams]);

  /**
   * 새로고침 처리
   */
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  /**
   * Excel 내보내기 처리
   */
  const handleExport = useCallback(async () => {
    try {
      const blob = await exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `chat-log-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel 내보내기 실패:", error);
    }
  }, [exportToExcel]);

  /**
   * 세션 상세 보기 처리
   */
  const handleViewSession = useCallback(
    (sessionId: string) => {
      navigate(`/chat/log/sessions/${sessionId}`);
    },
    [navigate]
  );

  return {
    rowsPerPage,
    filterValues,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleResetFilters,
    handleRefresh,
    handleExport,
    handleViewSession,
  };
};

export default useList;
